import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { supabase } from '@/lib/supabase'
import { AdminUser } from '@/lib/authService'

interface ImpersonationSession {
  id: string
  target_church_id: string
  target_church_name: string
  started_at: string
  expires_at: string
  reason: string
}

interface ImpersonationContextType {
  isImpersonating: boolean
  impersonationSession: ImpersonationSession | null
  startImpersonation: (churchId: string, churchName: string, reason: string) => Promise<boolean>
  endImpersonation: () => Promise<void>
  trackPageVisit: (pagePath: string) => Promise<void>
  timeRemaining: number // in minutes
}

const ImpersonationContext = createContext<ImpersonationContextType | undefined>(undefined)

export function ImpersonationProvider({ children, user }: { children: ReactNode; user: AdminUser | null }) {
  const [impersonationSession, setImpersonationSession] = useState<ImpersonationSession | null>(null)
  const [timeRemaining, setTimeRemaining] = useState<number>(0)

  // Check for existing active session on mount
  useEffect(() => {
    if (user?.adminLevel === 'super_admin') {
      checkActiveSession()
    }
  }, [user])

  // Update time remaining every minute
  useEffect(() => {
    if (!impersonationSession) return

    const updateTimeRemaining = () => {
      const expiresAt = new Date(impersonationSession.expires_at)
      const now = new Date()
      const diffMinutes = Math.max(0, Math.floor((expiresAt.getTime() - now.getTime()) / 60000))
      setTimeRemaining(diffMinutes)

      // Auto-end if expired
      if (diffMinutes === 0) {
        endImpersonation()
      }
    }

    updateTimeRemaining()
    const interval = setInterval(updateTimeRemaining, 60000) // Update every minute

    return () => clearInterval(interval)
  }, [impersonationSession])

  async function checkActiveSession() {
    try {
      const { data, error } = await supabase
        .from('active_impersonation_sessions')
        .select('*')
        .eq('super_admin_id', user?.id)
        .single()

      if (data && !error) {
        setImpersonationSession({
          id: data.id,
          target_church_id: data.target_church_id,
          target_church_name: data.target_church_name,
          started_at: data.started_at,
          expires_at: data.expires_at,
          reason: data.reason,
        })
      }
    } catch (error) {
      // No active session
      console.log('No active impersonation session')
    }
  }

  async function startImpersonation(
    churchId: string,
    churchName: string,
    reason: string
  ): Promise<boolean> {
    if (user?.adminLevel !== 'super_admin') {
      console.error('Only super admins can impersonate')
      return false
    }

    if (reason.length < 10) {
      console.error('Reason must be at least 10 characters')
      return false
    }

    try {
      const { data: sessionId, error } = await supabase.rpc('start_impersonation_session', {
        p_super_admin_id: user.id,
        p_super_admin_email: user.email,
        p_super_admin_name: `${user.firstName} ${user.lastName}`,
        p_target_church_id: churchId,
        p_target_church_name: churchName,
        p_reason: reason,
      })

      if (error) {
        console.error('Failed to start impersonation:', error)
        return false
      }

      // Get the full session details
      const { data: session } = await supabase
        .from('impersonation_sessions')
        .select('*')
        .eq('id', sessionId)
        .single()

      if (session) {
        setImpersonationSession({
          id: session.id,
          target_church_id: session.target_church_id,
          target_church_name: session.target_church_name,
          started_at: session.started_at,
          expires_at: session.expires_at,
          reason: session.reason,
        })

        // Store in localStorage for persistence across page reloads
        localStorage.setItem('impersonation_session', JSON.stringify({
          id: session.id,
          target_church_id: session.target_church_id,
          target_church_name: session.target_church_name,
          started_at: session.started_at,
          expires_at: session.expires_at,
          reason: session.reason,
        }))

        return true
      }

      return false
    } catch (error) {
      console.error('Error starting impersonation:', error)
      return false
    }
  }

  async function endImpersonation() {
    if (!impersonationSession) return

    try {
      await supabase.rpc('end_impersonation_session', {
        p_session_id: impersonationSession.id,
      })

      setImpersonationSession(null)
      localStorage.removeItem('impersonation_session')

      // Reload to clear any impersonated state
      window.location.reload()
    } catch (error) {
      console.error('Error ending impersonation:', error)
    }
  }

  async function trackPageVisit(pagePath: string) {
    if (!impersonationSession) return

    try {
      await supabase.rpc('track_impersonation_page', {
        p_session_id: impersonationSession.id,
        p_page_path: pagePath,
      })
    } catch (error) {
      console.error('Error tracking page visit:', error)
    }
  }

  const value: ImpersonationContextType = {
    isImpersonating: impersonationSession !== null,
    impersonationSession,
    startImpersonation,
    endImpersonation,
    trackPageVisit,
    timeRemaining,
  }

  return (
    <ImpersonationContext.Provider value={value}>
      {children}
    </ImpersonationContext.Provider>
  )
}

export function useImpersonation() {
  const context = useContext(ImpersonationContext)
  if (context === undefined) {
    throw new Error('useImpersonation must be used within an ImpersonationProvider')
  }
  return context
}
